import Component from '@glimmer/component';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import { hash } from '@ember/helper';
import { t } from 'ember-intl';
import { Button } from 'tio-ui/components/buttons';
import { on } from '@ember/modifier';
import type { ButtonArgs } from 'tio-ui/components/buttons';
import { PhPrinter } from 'ember-phosphor-icons';

interface TioPdfPrintButtonSignature {
  Args: {
    url: string;
    filename: string;
    appearance?: ButtonArgs['Args']['appearance'];
    intent?: ButtonArgs['Args']['intent'];
  };
  Blocks: {
    default: [
      {
        downloadAndOpenInNewTab: unknown;
        isRunning: boolean;
      },
    ];
  };
  Element: HTMLElement;
}

export default class TioPdfPrintButtonComponent extends Component<TioPdfPrintButtonSignature> {
  @service declare store: Store;

  @tracked fileUrl = '';

  downloadAndOpenInNewTab = dropTask(async (event) => {
    const url = this.args.url;

    if (!url) {
      return;
    }
    const adapter = this.store.adapterFor('application');
    const headers = adapter.headers;

    const response = await fetch(url, { headers: headers });

    if (response.ok) {
      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noreferrer noopener');
      const parent = event.target.parentElement;
      parent.appendChild(link);
      link.click();
      parent.removeChild(link);
    } else {
      console.error('Unable to download file due to an error.', response);
    }
  });

  get appearance(): ButtonArgs['Args']['appearance'] {
    return this.args.appearance || 'outlined';
  }

  get intent(): ButtonArgs['Args']['intent'] {
    return this.args.intent || 'primary';
  }

  <template>
    {{#if (has-block)}}
      {{yield
        (hash
          downloadAndOpenInNewTab=this.downloadAndOpenInNewTab.perform
          isRunning=this.downloadAndOpenInNewTab.isRunning
        )
      }}
    {{else}}
      <Button
        @appearance={{this.appearance}}
        @intent={{this.intent}}
        {{on "click" this.downloadAndOpenInNewTab.perform}}
        @isRunning={{this.downloadAndOpenInNewTab.isRunning}}
        ...attributes
      >
        <PhPrinter @size="16" class="mr-2" />
        {{t "common.print"}}
      </Button>
    {{/if}}
  </template>
}
